import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams, useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from '../components/NotificationBell';
import { 
    Building2, ChevronLeft, ChevronDown, ChevronRight,
    LayoutDashboard, Store, Landmark, Package, ArrowLeftRight,
    FileText, PiggyBank, BookOpen, Users, Settings, LogOut,
    Wallet, ShoppingCart, Receipt, ClipboardList,
    CreditCard, FileCheck, Building, Truck, MessageCircle,
    UserCog, Shield, Smartphone, Wrench, Search, Menu, X, Target
} from 'lucide-react';

// Lazy load contable pages
const ContableDashboard = lazy(() => import('./contable/Dashboard'));
const ContableStores = lazy(() => import('./contable/Stores'));
const ContableBanks = lazy(() => import('./contable/Banks'));
const ContableAssets = lazy(() => import('./contable/Assets'));
const ContableTransactions = lazy(() => import('./contable/Transactions'));
const ContableReports = lazy(() => import('./contable/Reports'));
const ContableBudgets = lazy(() => import('./contable/Budgets'));
const ContableAccounts = lazy(() => import('./contable/Accounts'));
const ContableUsers = lazy(() => import('./contable/Users'));

// Lazy load ordenes pages
const OrdenesAdminDashboard = lazy(() => import('./ordenes/AdminDashboard'));
const OrdenesMisOrdenes = lazy(() => import('./ordenes/MisOrdenes'));
const OrdenesGestion = lazy(() => import('./ordenes/GestionOrdenes'));
const OrdenesReportesActividad = lazy(() => import('./ordenes/ReportesActividad'));
const OrdenesTiendas = lazy(() => import('./ordenes/Tiendas'));
const OrdenesRoles = lazy(() => import('./ordenes/Roles'));

// Lazy load inventario pages
const InvDashboard = lazy(() => import('./inventario/Dashboard'));
const InvProductos = lazy(() => import('./inventario/Productos'));
const InvEntrada = lazy(() => import('./inventario/Entrada'));
const InvConsumo = lazy(() => import('./inventario/Consumo'));
const InvAjustes = lazy(() => import('./inventario/Ajustes'));
const InvTransferencias = lazy(() => import('./inventario/Transferencias'));
const InvInventarioFisico = lazy(() => import('./inventario/InventarioFisico'));
const InvReportes = lazy(() => import('./inventario/Reportes'));
const InvConfiguracion = lazy(() => import('./inventario/Configuracion'));

// Lazy load RRHH pages
const RRHHLayout = lazy(() => import('./rrhh/RRHHLayout'));
const RRHHDashboard = lazy(() => import('./rrhh/RRHHDashboard'));
const RRHHEmpleados = lazy(() => import('./rrhh/Empleados'));
const RRHHMovimientos = lazy(() => import('./rrhh/Movimientos'));
const RRHHPreparativos = lazy(() => import('./rrhh/PreparativosPlanilla'));
const RRHHCatalogos = lazy(() => import('./rrhh/Catalogos'));
const RRHHImportarCatalogos = lazy(() => import('./rrhh/ImportarCatalogos'));
const RRHHImportarEmpleados = lazy(() => import('./rrhh/ImportarEmpleados'));
const RRHHPlaceholder = lazy(() => import('./rrhh/PlaceholderRRHH'));

// Biblioteca
const BibliotecaPage = lazy(() => import('./biblioteca/Biblioteca'));

// Loading component
const PageLoader = () => (
    <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
    </div>
);

// Module configurations with their menus
const moduleConfigs = {
    'contable': {
        name: 'Sistema Contable',
        color: 'blue',
        menus: [
            { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard, path: '' },
            { id: 'tiendas', name: 'Tiendas', icon: Store, path: '/tiendas' },
            { id: 'bancos', name: 'Bancos', icon: Landmark, path: '/bancos' },
            { id: 'activos', name: 'Activos', icon: Package, path: '/activos' },
            { id: 'transacciones', name: 'Transacciones', icon: Receipt, path: '/transacciones' },
            { id: 'reportes', name: 'Reportes', icon: FileText, path: '/reportes' },
            { id: 'presupuestos', name: 'Presupuestos', icon: Target, path: '/presupuestos' },
            { id: 'cuentas', name: 'Cuentas', icon: BookOpen, path: '/cuentas' },
            { id: 'usuarios', name: 'Usuarios', icon: Users, path: '/usuarios' }
        ]
    },
    'rrhh': {
        name: 'Sistema RRHH',
        color: 'purple',
        menus: [
            { 
                id: 'catalogo', 
                name: 'Catálogo', 
                icon: BookOpen, 
                path: '/catalogo',
                submenu: [
                    { id: 'general', name: 'General', path: '/catalogo/general' },
                    { id: 'empresa', name: 'Empresa', path: '/catalogo/empresa' }
                ]
            },
            { id: 'empleados', name: 'Empleados', icon: Users, path: '/empleados' },
            { id: 'movimientos', name: 'Movimientos', icon: ArrowLeftRight, path: '/movimientos' },
            { id: 'reportes', name: 'Reportes', icon: FileText, path: '/reportes' }
        ]
    },
    'flujo-caja': {
        name: 'Flujo de Caja',
        color: 'emerald',
        menus: [
            { 
                id: 'operaciones', 
                name: 'Operaciones', 
                icon: LayoutDashboard, 
                path: '',
                submenu: [
                    { id: 'dashboard', name: 'Dashboard', path: '' },
                    { id: 'ventas', name: 'Ventas', path: '/ventas' },
                    { id: 'gastos', name: 'Gastos', path: '/gastos' },
                    { id: 'libro-caja', name: 'Libro de Caja', path: '/libro-caja' }
                ]
            },
            { id: 'caja', name: 'Caja', icon: Wallet, path: '/caja' },
            { id: 'reportes', name: 'Reportes', icon: FileText, path: '/reportes' },
            { 
                id: 'administracion', 
                name: 'Administración', 
                icon: Settings, 
                path: '/admin',
                submenu: [
                    { id: 'usuarios', name: 'Usuarios', path: '/admin/usuarios' },
                    { id: 'permisos', name: 'Permisos', path: '/admin/permisos' },
                    { id: 'tiendas', name: 'Tiendas', path: '/admin/tiendas' },
                    { id: 'dispositivos', name: 'Dispositivos', path: '/admin/dispositivos' }
                ]
            }
        ]
    },
    'pagos': {
        name: 'Portal de Pagos',
        color: 'amber',
        menus: [
            { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard, path: '' },
            { id: 'depositos', name: 'Registrar Depósito', icon: CreditCard, path: '/depositos' },
            { id: 'reportes', name: 'Reportes', icon: FileText, path: '/reportes' },
            { id: 'boletas', name: 'Boletas', icon: FileCheck, path: '/boletas' },
            { id: 'bancos', name: 'Bancos', icon: Landmark, path: '/bancos' },
            { id: 'proveedores', name: 'Proveedores', icon: Truck, path: '/proveedores' },
            { id: 'tiendas', name: 'Tiendas', icon: Store, path: '/tiendas' },
            { id: 'usuarios', name: 'Usuarios', icon: Users, path: '/usuarios' },
            { id: 'whatsapp', name: 'WhatsApp', icon: MessageCircle, path: '/whatsapp' },
            { id: 'configuracion', name: 'Configuración', icon: Settings, path: '/configuracion' }
        ]
    },
    'ordenes': {
        name: 'Órdenes de Trabajo',
        color: 'cyan',
        menus: [
            { id: 'dashboard', name: 'Dashboard KPIs', icon: LayoutDashboard, path: '' },
            { id: 'mis-ordenes', name: 'Mis Órdenes', icon: ClipboardList, path: '/mis-ordenes' },
            { id: 'gestion', name: 'Gestión de Órdenes', icon: Shield, path: '/gestion' },
            { id: 'reportes-actividad', name: 'Mis Actividades', icon: Target, path: '/reportes-actividad' },
            { id: 'tiendas', name: 'Tiendas', icon: Store, path: '/tiendas' },
            { id: 'roles', name: 'Roles', icon: UserCog, path: '/roles' }
        ]
    },
    'inventario': {
        name: 'Inventario',
        color: 'blue',
        menus: [
            { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard, path: '' },
            { id: 'productos', name: 'Productos', icon: Package, path: '/productos' },
            { id: 'entrada', name: 'Entradas', icon: ShoppingCart, path: '/entrada' },
            { id: 'consumo', name: 'Consumo / Salidas', icon: Truck, path: '/consumo' },
            { id: 'ajustes', name: 'Ajustes', icon: Wrench, path: '/ajustes' },
            { id: 'transferencias', name: 'Transferencias', icon: ArrowLeftRight, path: '/transferencias' },
            { id: 'inventario-fisico', name: 'Inventario Físico', icon: ClipboardList, path: '/inventario-fisico' },
            { id: 'reportes', name: 'Reportes', icon: FileText, path: '/reportes' },
            { id: 'configuracion', name: 'Configuración', icon: Settings, path: '/configuracion' }
        ]
    },
    'biblioteca': {
        name: 'Biblioteca de Herramientas',
        color: 'cyan',
        menus: [
            { id: 'archivos', name: 'Archivos', icon: BookOpen, path: '' }
        ]
    }
};

const colorClasses = {
    blue: { bg: 'bg-blue-600', hover: 'hover:bg-blue-700', text: 'text-blue-400', border: 'border-blue-500' },
    purple: { bg: 'bg-purple-600', hover: 'hover:bg-purple-700', text: 'text-purple-400', border: 'border-purple-500' },
    emerald: { bg: 'bg-emerald-600', hover: 'hover:bg-emerald-700', text: 'text-emerald-400', border: 'border-emerald-500' },
    amber: { bg: 'bg-amber-500', hover: 'hover:bg-amber-600', text: 'text-amber-400', border: 'border-amber-500' },
    cyan: { bg: 'bg-cyan-600', hover: 'hover:bg-cyan-700', text: 'text-cyan-400', border: 'border-cyan-500' }
};

// Contable module content with routes
const ContableContent = () => {
    return (
        <Suspense fallback={<PageLoader />}>
            <Routes>
                <Route path="/" element={<ContableDashboard />} />
                <Route path="/tiendas" element={<ContableStores />} />
                <Route path="/bancos" element={<ContableBanks />} />
                <Route path="/activos" element={<ContableAssets />} />
                <Route path="/transacciones" element={<ContableTransactions />} />
                <Route path="/reportes" element={<ContableReports />} />
                <Route path="/presupuestos" element={<ContableBudgets />} />
                <Route path="/cuentas" element={<ContableAccounts />} />
                <Route path="/usuarios" element={<ContableUsers />} />
            </Routes>
        </Suspense>
    );
};

// Ordenes module content with routes
const OrdenesContent = () => {
    return (
        <Suspense fallback={<PageLoader />}>
            <Routes>
                <Route path="/" element={<OrdenesAdminDashboard />} />
                <Route path="/mis-ordenes" element={<OrdenesMisOrdenes />} />
                <Route path="/gestion" element={<OrdenesGestion />} />
                <Route path="/reportes-actividad" element={<OrdenesReportesActividad />} />
                <Route path="/tiendas" element={<OrdenesTiendas />} />
                <Route path="/roles" element={<OrdenesRoles />} />
            </Routes>
        </Suspense>
    );
};

// Inventario module content with routes
const InventarioContent = () => {
    return (
        <Suspense fallback={<PageLoader />}>
            <Routes>
                <Route path="/" element={<InvDashboard />} />
                <Route path="/productos" element={<InvProductos />} />
                <Route path="/entrada" element={<InvEntrada />} />
                <Route path="/consumo" element={<InvConsumo />} />
                <Route path="/ajustes" element={<InvAjustes />} />
                <Route path="/transferencias" element={<InvTransferencias />} />
                <Route path="/inventario-fisico" element={<InvInventarioFisico />} />
                <Route path="/reportes" element={<InvReportes />} />
                <Route path="/configuracion" element={<InvConfiguracion />} />
            </Routes>
        </Suspense>
    );
};

// RRHH module content with horizontal nav layout
const RRHHContent = () => {
    return (
        <Suspense fallback={<PageLoader />}>
            <RRHHLayout>
                <Routes>
                    <Route path="/" element={<RRHHDashboard />} />
                    <Route path="/empleados" element={<RRHHEmpleados />} />
                    <Route path="/importar-empleados" element={<RRHHImportarEmpleados />} />
                    <Route path="/movimientos" element={<RRHHMovimientos />} />
                    <Route path="/movimientos/ausencias" element={<RRHHMovimientos filterType="ausencia" />} />
                    <Route path="/movimientos/suspensiones" element={<RRHHMovimientos filterType="suspension" />} />
                    <Route path="/movimientos/aumentos" element={<RRHHMovimientos filterType="aumento" />} />
                    <Route path="/movimientos/vacaciones" element={<RRHHMovimientos filterType="vacaciones" />} />
                    <Route path="/movimientos/finiquitos" element={<RRHHMovimientos filterType="finiquito" />} />
                    <Route path="/movimientos/aguinaldo" element={<RRHHMovimientos filterType="aguinaldo" />} />
                    <Route path="/movimientos/bono14" element={<RRHHMovimientos filterType="bono14" />} />
                    <Route path="/movimientos/ingreso" element={<RRHHPreparativos section="ingreso" />} />
                    <Route path="/movimientos/descuento" element={<RRHHPreparativos section="descuento" />} />
                    <Route path="/movimientos/preplanilla" element={<RRHHPreparativos section="preplanilla" />} />
                    <Route path="/movimientos/cierre-planilla" element={<RRHHPreparativos section="cierre-planilla" />} />
                    <Route path="/movimientos/reapertura-planilla" element={<RRHHPreparativos section="reapertura-planilla" />} />
                    <Route path="/movimientos/horas-extras" element={<RRHHPreparativos section="horas-extras" />} />
                    <Route path="/movimientos/dias-horas-septimo" element={<RRHHPreparativos section="dias-horas-septimo" />} />
                    <Route path="/catalogos/general" element={<RRHHCatalogos section="general" />} />
                    <Route path="/catalogos/empresa" element={<RRHHCatalogos section="empresa" />} />
                    <Route path="/catalogos/importar" element={<RRHHImportarCatalogos />} />
                    <Route path="/reportes/*" element={<RRHHPlaceholder title="Reportes RRHH" />} />
                    <Route path="/organigramas" element={<RRHHPlaceholder title="Organigramas" />} />
                    <Route path="/seguridad" element={<RRHHPlaceholder title="Seguridad" />} />
                </Routes>
            </RRHHLayout>
        </Suspense>
    );
};

// Placeholder content for modules not yet integrated
const PlaceholderContent = ({ moduleName, sectionName }) => (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
        <div className="text-center py-12">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                {moduleName} - {sectionName || 'Dashboard'}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                Este módulo está pendiente de integración. Próximamente estará disponible.
            </p>
        </div>
    </div>
);

const ModulePage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, hasPermission, hasSubPermission } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [expandedMenus, setExpandedMenus] = useState({});
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const moduleConfig = moduleConfigs[slug];
    const colors = colorClasses[moduleConfig?.color] || colorClasses.blue;

    // Redirect to first allowed sub-page if user doesn't have permission for the default page
    useEffect(() => {
        if (!moduleConfig || !slug) return;
        const currentSubPath = location.pathname.replace(`/modulo/${slug}`, '') || '';
        
        if (currentSubPath === '' || currentSubPath === '/') {
            const defaultMenu = moduleConfig.menus.find(m => m.path === '');
            if (defaultMenu && !hasSubPermission(slug, defaultMenu.id)) {
                const firstAllowed = moduleConfig.menus.find(m => m.path !== '' && hasSubPermission(slug, m.id));
                if (firstAllowed) {
                    navigate(`/modulo/${slug}${firstAllowed.path}`, { replace: true });
                }
            }
        } else {
            const currentMenu = moduleConfig.menus.find(m => m.path === currentSubPath);
            if (currentMenu && !hasSubPermission(slug, currentMenu.id)) {
                const firstAllowed = moduleConfig.menus.find(m => hasSubPermission(slug, m.id));
                if (firstAllowed) {
                    navigate(`/modulo/${slug}${firstAllowed.path}`, { replace: true });
                }
            }
        }
    }, [slug, location.pathname, moduleConfig, hasSubPermission, navigate]);

    if (!moduleConfig) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Módulo no encontrado</h2>
                    <button
                        onClick={() => navigate('/')}
                        className="text-blue-600 hover:text-blue-500"
                    >
                        Volver al inicio
                    </button>
                </div>
            </div>
        );
    }

    if (!hasPermission(slug)) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Sin acceso</h2>
                    <p className="text-slate-500 dark:text-slate-400 mb-4">No tienes permiso para acceder a este módulo.</p>
                    <button
                        onClick={() => navigate('/')}
                        className="text-blue-600 hover:text-blue-500"
                    >
                        Volver al inicio
                    </button>
                </div>
            </div>
        );
    }

    const currentSubPath = location.pathname.replace(`/modulo/${slug}`, '') || '';

    const toggleSubmenu = (menuId) => {
        setExpandedMenus(prev => ({
            ...prev,
            [menuId]: !prev[menuId]
        }));
    };

    const currentPath = location.pathname.replace(`/modulo/${slug}`, '') || '';

    const isActive = (menuPath, submenu) => {
        if (submenu) {
            return submenu.some(sub => currentPath === sub.path);
        }
        return currentPath === menuPath;
    };

    const renderMenuItem = (menu) => {
        const Icon = menu.icon;
        const hasSubmenu = menu.submenu && menu.submenu.length > 0;
        const isExpanded = expandedMenus[menu.id];
        const active = isActive(menu.path, menu.submenu);

        return (
            <div key={menu.id}>
                <button
                    onClick={() => {
                        if (hasSubmenu) {
                            toggleSubmenu(menu.id);
                        } else {
                            navigate(`/modulo/${slug}${menu.path}`);
                            setMobileMenuOpen(false);
                        }
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                        active 
                            ? `${colors.bg} text-white shadow-lg` 
                            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white'
                    }`}
                >
                    <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5" />
                        {sidebarOpen && <span className="font-medium">{menu.name}</span>}
                    </div>
                    {hasSubmenu && sidebarOpen && (
                        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    )}
                </button>

                {hasSubmenu && isExpanded && sidebarOpen && (
                    <div className="ml-8 mt-1 space-y-1">
                        {menu.submenu.map(sub => (
                            <button
                                key={sub.id}
                                onClick={() => {
                                    navigate(`/modulo/${slug}${sub.path}`);
                                    setMobileMenuOpen(false);
                                }}
                                className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-all ${
                                    currentPath === sub.path
                                        ? `${colors.text} bg-slate-100 dark:bg-slate-700`
                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                                }`}
                            >
                                {sub.name}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Render module-specific content
    const renderModuleContent = () => {
        if (slug === 'contable') {
            return <ContableContent key="contable" />;
        }
        
        if (slug === 'ordenes') {
            return <OrdenesContent key="ordenes" />;
        }

        if (slug === 'inventario') {
            return <InventarioContent key="inventario" />;
        }

        if (slug === 'biblioteca') {
            return (
                <Suspense fallback={<PageLoader />}>
                    <BibliotecaPage />
                </Suspense>
            );
        }
        
        // For other modules, show placeholder
        return <PlaceholderContent moduleName={moduleConfig.name} sectionName={currentPath.replace('/', '').replace(/-/g, ' ')} />;
    };

    // RRHH uses its own horizontal nav layout, bypass the sidebar
    if (slug === 'rrhh') {
        return <RRHHContent key="rrhh" />;
    }

    return (
        <div key={slug} className="min-h-screen bg-slate-50 dark:bg-slate-900 flex" data-testid={`module-page-${slug}`}>
            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed lg:static inset-y-0 left-0 z-50
                ${sidebarOpen ? 'w-64' : 'w-20'} 
                ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 flex flex-col transition-all duration-300 shadow-xl lg:shadow-none
            `}>
                {/* Sidebar Header */}
                <div className="h-16 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-4">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center`}>
                            <Building2 className="w-5 h-5 text-white" />
                        </div>
                        {sidebarOpen && (
                            <div>
                                <h2 className="text-sm font-bold text-slate-900 dark:text-white">SIEMPRE</h2>
                                <p className="text-xs text-slate-500 dark:text-slate-400">{moduleConfig.name}</p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => setMobileMenuOpen(false)}
                        className="lg:hidden p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Back to Dashboard */}
                <div className="p-3 border-b border-slate-200 dark:border-slate-700">
                    <button
                        onClick={() => navigate('/')}
                        className="w-full flex items-center gap-2 px-4 py-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-all"
                        data-testid="back-to-dashboard"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        {sidebarOpen && <span className="text-sm font-medium">Volver al Portal</span>}
                    </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                    {moduleConfig.menus.filter(menu => hasSubPermission(slug, menu.id)).map(renderMenuItem)}
                </nav>

                {/* User Section */}
                <div className="p-3 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3 px-3 py-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white text-xs font-medium">
                            {user?.name?.charAt(0) || user?.full_name?.charAt(0) || 'U'}
                        </div>
                        {sidebarOpen && (
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{user?.name || user?.full_name}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => {
                            logout();
                            navigate('/login');
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 mt-2 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-all"
                    >
                        <LogOut className="w-4 h-4" />
                        {sidebarOpen && <span className="text-sm">Cerrar Sesión</span>}
                    </button>
                </div>

                {/* Collapse Button */}
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-white shadow-sm"
                >
                    {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top Bar */}
                <header className="h-16 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-between px-4 lg:px-6">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setMobileMenuOpen(true)}
                            className="lg:hidden p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                        <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                            <Search className="w-4 h-4 text-slate-400" />
                            <input 
                                type="text" 
                                placeholder="Buscar..." 
                                className="bg-transparent text-sm text-slate-700 dark:text-slate-200 placeholder-slate-400 outline-none w-48"
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <NotificationBell />
                    </div>
                </header>

                {/* Page Content */}
                <main className="flex-1 p-4 lg:p-6 overflow-auto">
                    {renderModuleContent()}
                </main>
            </div>
        </div>
    );
};

export default ModulePage;
